// TEMPORARY MOCK ADAPTER for Biometric Data (PRD 09 / FEAT-032).
// In-memory only — state lives in this module's `store` and resets on hard reload.
// Mutations resolve with ~300 ms simulated latency. Errors mimic the backend envelope.
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter. See biometric.adapter.ts.

import {
  BiometricApiError,
  BIOMETRIC_KEYS,
  type BiometricApi,
  type BiometricKey,
  type BiometricRange,
  type BiometricRangesResponse,
  type BiometricRangeUpdateInput,
} from '../types/biometric.types';
import { BIOMETRIC_DEFAULTS, BIOMETRIC_META } from '../utils/biometric.defaults';

const LATENCY_MS = 300;

const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const isValidKey = (key: string): key is BiometricKey =>
  (BIOMETRIC_KEYS as readonly string[]).includes(key);

const validateRange = (key: BiometricKey, range: BiometricRange): void => {
  const { absMin, absMax } = BIOMETRIC_META[key];
  const fields: Record<string, string> = {};
  (['warnLow', 'normalMin', 'normalMax', 'warnHigh'] as const).forEach((f) => {
    const v = range[f];
    if (!Number.isFinite(v) || v < absMin || v > absMax) {
      fields[f] = `유효한 범위를 입력해주세요 (${absMin} ~ ${absMax}).`;
    }
  });
  if (Object.keys(fields).length > 0) {
    // Dev-only abs-bounds guard; the real BE has no distinct range-bounds code — surface as UNKNOWN.
    throw new BiometricApiError(
      'UNKNOWN',
      '유효하지 않은 값이 있습니다.',
      fields,
    );
  }
  if (
    !(
      range.warnLow < range.normalMin &&
      range.normalMin < range.normalMax &&
      range.normalMax < range.warnHigh
    )
  ) {
    throw new BiometricApiError(
      'BIOMETRIC_THRESHOLD_ORDER',
      '임계값 순서가 올바르지 않습니다. (주의 하한 < 정상 최소 < 정상 최대 < 주의 상한)',
      {
        warnLow: '순서 오류',
        normalMin: '순서 오류',
        normalMax: '순서 오류',
        warnHigh: '순서 오류',
      },
    );
  }
};

// In-memory store seeded from defaults
let store: BiometricRangesResponse = clone(
  BIOMETRIC_DEFAULTS,
) as BiometricRangesResponse;

export const biometricMockApi: BiometricApi = {
  async getRanges(): Promise<BiometricRangesResponse> {
    await delay();
    return clone(store);
  },

  async updateRange({
    key,
    range,
  }: BiometricRangeUpdateInput): Promise<BiometricRange> {
    await delay();
    if (!isValidKey(key)) {
      throw new BiometricApiError(
        'NOT_FOUND',
        '해당 생체 데이터 항목을 찾을 수 없습니다.',
      );
    }
    validateRange(key, range);
    store = { ...store, [key]: clone(range) };
    return clone(range);
  },
};
